"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import Lenis from "lenis";

// ─── Constants ──────────────────────────────────────────────────────────────
/** How long (ms) of no scroll events before the thumb disappears when idle. */
const IDLE_TIMEOUT_MS = 800;

// ─── Component ──────────────────────────────────────────────────────────────
export default function LenisProvider(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const thumbRef = useRef(null);
  const trackRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lenisRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const pathname = usePathname();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, {
          immediate: true
        });
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname) {
    t2 = [pathname];
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (typeof window !== "undefined") {
        const originalLog = console.log;
        const originalWarn = console.warn;
        console.log = (...t5) => {
          const args = t5;
          if (typeof args[0] === "string" && (args[0].startsWith("THREE.") || args[0].includes("WebGLRenderer"))) {
            return;
          }
          originalLog(...args);
        };
        console.warn = (...t6) => {
          const args_0 = t6;
          if (typeof args_0[0] === "string" && (args_0[0].startsWith("THREE.") || args_0[0].includes("WebGLRenderer") || args_0[0].includes("preloaded using link preload"))) {
            return;
          }
          originalWarn(...args_0);
        };
      }
      const lenis = new Lenis({
        smoothWheel: true,
        lerp: 0.1
      });
      lenisRef.current = lenis;
      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);
      const thumb = thumbRef.current;
      const track = trackRef.current;
      if (!thumb || !track) {
        return;
      }
      const showThumb = () => {
        thumb.style.opacity = "1";
      };
      const hideThumb = () => {
        if (!isDraggingRef.current) {
          thumb.style.opacity = "0";
        }
      };
      const resetIdleTimer = () => {
        if (idleTimerRef.current !== null) {
          clearTimeout(idleTimerRef.current);
        }
        if (!isDraggingRef.current) {
          idleTimerRef.current = setTimeout(hideThumb, IDLE_TIMEOUT_MS);
        }
      };
      if (typeof window !== "undefined") {
        (window as unknown as {
          lenis: Lenis;
        }).lenis = lenis;
      }
      let velocityRef = 0;
      lenis.on("scroll", t7 => {
        const {
          velocity,
          progress
        } = t7;
        velocityRef = Math.abs(velocity);
        const travelPercent = 88 * progress;
        thumb.style.transform = `translateY(${travelPercent}vh)`;
        showThumb();
        resetIdleTimer();
      });
      const hasMagnetizedSet = new WeakSet();
      const magnetObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && velocityRef < 1.5 && !hasMagnetizedSet.has(entry.target)) {
            hasMagnetizedSet.add(entry.target);
            const rect = entry.target.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const currentScroll = lenis.scroll;
            const elTop = currentScroll + rect.top;
            let targetY = elTop - (viewportHeight / 2 - rect.height / 2);
            if (rect.height >= viewportHeight * 0.85) {
              targetY = elTop;
            }
            lenis.scrollTo(targetY, {
              duration: 1.2,
              easing: _temp
            });
          }
        });
      }, {
        threshold: 0.6
      });
      const magnetTargets = document.querySelectorAll("#contact, .projects-grid-magnet");
      magnetTargets.forEach(target => magnetObserver.observe(target));
      const updateScrollFromPointer = clientY => {
        if (!lenisRef.current) {
          return;
        }
        const thumbHeightPx = window.innerHeight * 0.12;
        const availableTrackHeight = window.innerHeight - thumbHeightPx;
        if (availableTrackHeight <= 0) {
          return;
        }
        const targetThumbTop = clientY - dragOffsetRef.current;
        const progress_0 = Math.max(0, Math.min(1, targetThumbTop / availableTrackHeight));
        const targetScroll = progress_0 * lenisRef.current.limit;
        lenisRef.current.scrollTo(targetScroll, {
          immediate: true
        });
      };
      const handlePointerMove = e => {
        if (!isDraggingRef.current) {
          return;
        }
        showThumb();
        updateScrollFromPointer(e.clientY);
      };
      const handlePointerUp = () => {
        if (!isDraggingRef.current) {
          return;
        }
        isDraggingRef.current = false;
        document.body.classList.remove("is-scrolling-drag");
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
        resetIdleTimer();
      };
      const handlePointerDown = e_0 => {
        if (e_0.pointerType === "touch") {
          return;
        }
        e_0.preventDefault();
        isDraggingRef.current = true;
        showThumb();
        if (idleTimerRef.current !== null) {
          clearTimeout(idleTimerRef.current);
        }
        document.body.classList.add("is-scrolling-drag");
        const thumbRect = thumb.getBoundingClientRect();
        const thumbHeightPx_0 = window.innerHeight * 0.12;
        if (e_0.clientY >= thumbRect.top && e_0.clientY <= thumbRect.bottom) {
          dragOffsetRef.current = e_0.clientY - thumbRect.top;
        } else {
          dragOffsetRef.current = thumbHeightPx_0 / 2;
        }
        updateScrollFromPointer(e_0.clientY);
        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);
      };
      const handlePointerEnter = () => {
        showThumb();
      };
      const handlePointerLeave = () => {
        if (!isDraggingRef.current) {
          resetIdleTimer();
        }
      };
      track.addEventListener("pointerdown", handlePointerDown);
      track.addEventListener("pointerenter", handlePointerEnter);
      track.addEventListener("pointerleave", handlePointerLeave);
      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
        lenisRef.current = null;
        if (idleTimerRef.current !== null) {
          clearTimeout(idleTimerRef.current);
        }
        document.body.classList.remove("is-scrolling-drag");
        track.removeEventListener("pointerdown", handlePointerDown);
        track.removeEventListener("pointerenter", handlePointerEnter);
        track.removeEventListener("pointerleave", handlePointerLeave);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: "16px"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div ref={trackRef} id="brutalist-scrollbar-track" aria-hidden="true" className="hidden md:block fixed top-0 right-0 h-screen z-[9998] pointer-events-auto cursor-grab active:cursor-grabbing select-none" style={t5}><div ref={thumbRef} id="brutalist-scrollbar-thumb" className="absolute top-0 right-0 w-full cursor-grab active:cursor-grabbing" style={{
        height: "12vh",
        opacity: 0
      }} /></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children) {
    t7 = <>{children}{t6}</>;
    $[7] = children;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}
function _temp(t) {
  return 1 - Math.pow(1 - t, 3);
}